import { useMemo } from 'react';

import { type ListAddOnsResponse } from '@air/api';

import { useCurrentWorkspaceAddOns } from '~/swr-hooks/workspaces/addOns/useCurrentWorkspaceAddOns';

export type PrivateAddOnFeatures = {
  libraries?: boolean;
  assetTransforms?: Extract<ListAddOnsResponse['data'][number], { key: 'assetTransforms' }>['settings'] | boolean;
  agentSessions?: boolean;
};

type GetPrivateAddOnFeaturesParams = {
  addOnsData: ListAddOnsResponse['data'] | undefined | null;
  isPending: boolean;
};

const DEFAULT_PRIVATE_ADD_ON_FEATURES: PrivateAddOnFeatures = {
  libraries: undefined,
  assetTransforms: undefined,
  agentSessions: undefined,
};

export const getPrivateAddOnFeatures = ({
  addOnsData,
  isPending,
}: GetPrivateAddOnFeaturesParams): PrivateAddOnFeatures => {
  if (isPending || !addOnsData) {
    return DEFAULT_PRIVATE_ADD_ON_FEATURES;
  }

  const librariesAddOn = addOnsData.find((addOn) => addOn.key === 'libraries');
  const assetTransformsAddOn = addOnsData.find((addOn) => addOn.key === 'assetTransforms');
  const agentSessionsAddOn = addOnsData.find((addOn) => addOn.key === 'agentSessions');

  return {
    libraries: librariesAddOn ? librariesAddOn.enabled === true && librariesAddOn.visible === true : false,
    assetTransforms:
      assetTransformsAddOn && assetTransformsAddOn.enabled === true && assetTransformsAddOn.visible === true
        ? assetTransformsAddOn.settings
        : false,
    agentSessions: agentSessionsAddOn
      ? agentSessionsAddOn.enabled === true && agentSessionsAddOn.visible === true
      : false,
  };
};

export const usePrivateAddOnFeatures = (): PrivateAddOnFeatures => {
  const { data: addOns, isPending } = useCurrentWorkspaceAddOns({ enabled: true });

  return useMemo(() => getPrivateAddOnFeatures({ addOnsData: addOns?.data, isPending }), [isPending, addOns?.data]);
};
